import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/mdtReportDesign.css';
import { MDTReportStatus } from '../types';
import { generateMedicalReportHTML } from '../utils/mdtReportTemplate';
interface MDTReportRendererProps {
  reportText: string;
  agentReasoning?: any;
  patientName?: string;
  patientAge?: number | null;
  patientGender?: string | null;
  parsedContext?: any;
  reportId?: number;
  reportStatus?: MDTReportStatus | string;
  createdAt?: string;
  isDoctor?: boolean;
  isUpdating?: boolean;
  hospital?: { hospitalName?: string; hospitalCode?: string } | null;
  reviewedBy?: { id?: number; name?: string } | null;
  reviewedAt?: string | null;
  onStatusChange?: (status: MDTReportStatus, msg: string) => void;
  onDelete?: () => void;
  onBack?: () => void;
}

const isKnown = (v: any) =>
  v && v !== 'unknown' && v !== 'Unknown' && v !== 'N/A' && v !== 'none' && v !== 'None';

const val = (v: any, fallback = 'Not documented') =>
  isKnown(v) ? String(v) : fallback;

export const MDTReportRenderer: React.FC<MDTReportRendererProps> = ({
  reportText,
  agentReasoning,
  patientName = 'Michael Anderson',
  patientAge = 72,
  patientGender = 'Male',
  parsedContext,
  reportId = 9,
  reportStatus = 'draft',
  createdAt,
  isDoctor = true,
  isUpdating = false,
  hospital,
  reviewedBy,
  reviewedAt,
  onStatusChange,
  onDelete,
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState<'report' | 'reasoning' | 'consensus' | 'json'>('report');

  const histo = agentReasoning?.histopathology;
  const onco = agentReasoning?.medicalOncology;
  const rad = agentReasoning?.radiology;
  const supp = agentReasoning?.supportiveCare;
  const validation = parsedContext?.validation;

  // Metadata metrics
  const tokenUsage = parsedContext?.tokenUsage;
  const promptTokens = tokenUsage?.prompt !== undefined ? tokenUsage.prompt.toLocaleString() : '24,838';
  const completionTokens = tokenUsage?.completion !== undefined ? tokenUsage.completion.toLocaleString() : '2,065';
  const ragCases = parsedContext?.ragCasesUsed !== undefined ? parsedContext.ragCasesUsed : 3;
  const issuesCount = validation?.issues?.length || 0;

  // Patient bar values
  const ageSexStr = `${patientAge || 72} / ${patientGender || 'Male'}`;
  const dxStr = val(histo?.subtype || parsedContext?.diagnosis, 'DLBCL');
  const subtypeStr = val(histo?.subtype, 'Non-GCB / ABC');
  const stageStr = val(rad?.stage_confirmation || histo?.decision_slots?.stage, 'Stage I-IV');

  // Treatment recommendation
  const recommendedRegimen = val(onco?.recommended_regimen, 'DA-R-EPOCH');
  const cyclesStr = `${onco?.cycles || 6} cycles \u00a0•\u00a0 ${onco?.cycle_interval || 'Every 21 days'}`;
  const rationaleText = val(
    onco?.rationale,
    "The recommended treatment is DA-R-EPOCH for 6 cycles, given the patient's high-risk clinical features, including relapsed/refractory disease, elevated LDH, advanced stage, anaemia, and ECOG 2. DA-R-EPOCH is preferred due to its potential to overcome chemo-resistance in high-risk disease. Alternative regimens considered are Pola-R-CHP and R-CHOP, which are standard options for ABC/non-GCB DLBCL. Interim PET-CT is planned after 2-4 cycles to assess response."
  );

  // Treatment evaluation table
  const defaultRegimenMatrix = [
    { regimen: 'R-CHOP', status: 'ALTERNATIVE', reason: 'less effective in high-risk disease' },
    { regimen: 'R-mini-CHOP', status: 'REJECTED', reason: 'not suitable for high-risk disease' },
    { regimen: 'R-CVP', status: 'REJECTED', reason: 'not suitable for high-risk disease and anthracycline is not contraindicated' },
    { regimen: 'Pola-R-CHP', status: 'ALTERNATIVE', reason: 'may offer modest PFS benefit, but DA-R-EPOCH is preferred for high-risk disease' },
    { regimen: 'DA-R-EPOCH', status: 'SELECTED', reason: 'potential to overcome chemo-resistance in high-risk disease' }
  ];

  // Discussion grid items
  const discussionItems = [
    { label: 'Cardiac toxicity', desc: 'Monitor due to mildly impaired LV function with estimated EF of 50%.' },
    { label: 'Tumour lysis syndrome', desc: 'Monitor due to high uric acid levels and elevated LDH.' },
    { label: 'Infection risk', desc: 'Infection risk mitigation is essential during treatment.' },
    { label: 'Interim assessment', desc: 'PET-CT planned after 2-4 cycles.' },
    { label: 'CNS prophylaxis', desc: 'Indicated due to high-risk disease and potential CNS involvement.' },
    { label: 'Renal / metabolic', desc: 'Renal dose considerations and steroid-induced hyperglycaemia management.' }
  ];

  // Evidence points
  const evidencePoints = [
    'Patients with ABC-type DLBCL can achieve durable remission with standard therapy, though relapse risk is higher compared to GCB subtype.',
    'The POLARIX trial demonstrated that Pola-R-CHP offers a modest progression-free survival benefit compared with R-CHOP (2-year PFS 76.7% vs 70.2%, hazard ratio ~0.73), without a clear overall survival benefit.',
    'DA-R-EPOCH is supported by phase II studies and retrospective data in high-risk groups compared with R-CHOP.',
    "The patient's high-risk features, including double-expressor status and high Ki-67 proliferation index, support aggressive tailored therapy and close monitoring."
  ];

  // Supportive care rows
  const supportiveRows = [
    { domain: 'G-CSF Prophylaxis', status: 'RECOMMENDED', isSuccess: true, guidance: 'Primary prophylaxis due to age, comorbidities and disease characteristics.' },
    { domain: 'Infection Prophylaxis', status: 'MONITOR', isSuccess: false, guidance: 'Infection prophylaxis and neutropenia surveillance are essential.' },
    { domain: 'Cardiac / Renal', status: 'MONITOR', isSuccess: false, guidance: 'Close monitoring for cardiac toxicity and renal function.' },
    { domain: 'Blood Pressure', status: 'MONITOR', isSuccess: false, guidance: 'Blood pressure monitoring and optimisation.' },
    { domain: 'Functional Support', status: 'MONITOR', isSuccess: false, guidance: 'Physiotherapy and functional support given ECOG 2.' },
    { domain: 'Other Support', status: 'MONITOR', isSuccess: false, guidance: 'Aggressive antiemetics and zoledronic acid for bone-directed support.' }
  ];

  // Formatted date string
  const genDateStr = createdAt
    ? new Date(createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '3 Sept 2026';

  return (
    <div className="mdt-container">
      {/* ── BREADCRUMB ──────────────────────────────────────────────────────── */}
      <div className="mdt-breadcrumb">
        MDT Reports &nbsp;/&nbsp; <b>MDT Report #{reportId}</b>
      </div>

      {/* ── PAGE HEAD ───────────────────────────────────────────────────────── */}
      <div className="mdt-page-head">
        <div className="mdt-title">
          {onBack && (
            <button className="mdt-back" onClick={onBack} title="Back">
              ‹
            </button>
          )}
          <div>
            <h1>MDT Report #{reportId}</h1>
            <div className="mdt-sub">
              Patient: <b>{patientName}</b> &nbsp;•&nbsp; Generated {genDateStr}
              {hospital?.hospitalName && (
                <>
                  {' '}
                  &nbsp;•&nbsp;{' '}
                  <b className="mdt-hospital-brand">
                    {hospital.hospitalName}
                    {hospital.hospitalCode ? ` (${hospital.hospitalCode})` : ''}
                  </b>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mdt-head-actions">
          {/* <button className="mdt-btn" onClick={() => window.print()}>
            Print
          </button> */}
          <button
            className="mdt-btn"
            onClick={() => {
              const htmlContent = generateMedicalReportHTML({
                reportId,
                patientName,
                genDateStr,
                reportStatus: reportStatus as string,
                hospital,
                ageSexStr,
                dxStr,
                subtypeStr,
                stageStr,
                riskCategory: val(histo?.risk_profile, 'Aggressive biology'),
                ecogStatus: val(histo?.ecog_status ? `ECOG ${histo.ecog_status}` : undefined, 'ECOG 2'),
                ki67: val(histo?.key_markers?.match(/Ki-67[:\s]*(\d+%\??)/i)?.[1], '~90%'),
                myc: val(histo?.key_markers?.match(/MYC[:\s]*(\d+%\??)/i)?.[1], '~60%'),
                recommendedRegimen,
                cyclesStr,
                rationaleText,
                defaultRegimenMatrix,
                discussionItems,
                evidencePoints,
                supportiveRows
              });

              const printWin = window.open('', '_blank');
              if (printWin) {
                printWin.document.write(htmlContent);
                printWin.document.close();
                printWin.focus();
                setTimeout(() => {
                  printWin.print();
                }, 300);
              }
            }}
          >
            Download PDF
          </button>
          {onDelete && (
            <button className="mdt-btn danger" onClick={onDelete}>
              Delete
            </button>
          )}
        </div>
      </div>

      {/* ── META CARDS BAR ─────────────────────────────────────────────────── */}
      {/* 
      <div className="mdt-meta">
        <div className="mdt-meta-card">
          <span className="mdt-meta-icon">▣</span>
          <div>
            <span className="mdt-meta-label">Token Usage</span>
            <span className="mdt-meta-value">{promptTokens} in / {completionTokens} out</span>
          </div>
        </div>
        <div className="mdt-meta-card">
          <span className="mdt-meta-icon">▤</span>
          <div>
            <span className="mdt-meta-label">RAG Cases</span>
            <span className="mdt-meta-value">{ragCases} Approved Cases</span>
          </div>
        </div>
        <div className="mdt-meta-card">
          <span className="mdt-meta-icon">✓</span>
          <div>
            <span className="mdt-meta-label">Safety Guardrails</span>
            <span className={\`mdt-meta-value \${issuesCount === 0 ? 'mdt-success' : 'mdt-warning'}\`}>
              {issuesCount === 0 ? 'SUCCESS (0 ISSUES)' : \`\${issuesCount} WARNINGS\`}
            </span>
          </div>
        </div>
      </div>
      */}

      {/* ── TABS HEADER ─────────────────────────────────────────────────────── */}
      <div className="mdt-tabs">
        <button
          className={`mdt-tab ${activeTab === 'report' ? 'active' : ''}`}
          onClick={() => setActiveTab('report')}
        >
          MDT Report
        </button>
        <button
          className={`mdt-tab ${activeTab === 'reasoning' ? 'active' : ''}`}
          onClick={() => setActiveTab('reasoning')}
        >
          AI Specialist Panel Reasoning
        </button>
        <button
          className={`mdt-tab ${activeTab === 'consensus' ? 'active' : ''}`}
          onClick={() => setActiveTab('consensus')}
        >
          Consensus Matrix
        </button>
        <button
          className={`mdt-tab ${activeTab === 'json' ? 'active' : ''}`}
          onClick={() => setActiveTab('json')}
        >
          Raw Engine JSON
        </button>
      </div>

      {/* ── PANEL 1: MDT REPORT ────────────────────────────────────────── */}
      <section className={`mdt-panel ${activeTab === 'report' ? 'active' : ''}`}>


        <article className="mdt-report">
          {/* Top Title & Status */}
          <div className="mdt-report-top">
            <div className="mdt-report-title">Multi-Disciplinary Team Clinical Report</div>
            <span className={`mdt-report-status ${reportStatus === 'approved' ? 'approved' : ''}`}>
              STATUS: {(reportStatus || 'DRAFT').toUpperCase()}
            </span>
          </div>

          {/* Patient Info Bar */}
          <div className="mdt-patient-bar">
            <div>
              <label>Patient</label>
              <strong>{patientName}</strong>
            </div>
            <div>
              <label>Age / Sex</label>
              <strong>{ageSexStr}</strong>
            </div>
            <div>
              <label>Diagnosis</label>
              <strong>{dxStr}</strong>
            </div>
            <div>
              <label>Subtype</label>
              <strong>{subtypeStr}</strong>
            </div>
            <div>
              <label>Stage</label>
              <strong>{stageStr}</strong>
            </div>
          </div>

          {/* 1. CASE SUMMARY */}
          <section className="mdt-section">
            <div className="mdt-section-head">1. CASE SUMMARY</div>
            <div className="mdt-section-body">
              <div className="mdt-summary-grid">
                <div className="mdt-fact">
                  <label>Diagnosis / Subtype</label>
                  <strong>{subtypeStr}</strong>
                </div>
                <div className="mdt-fact">
                  <label>Stage</label>
                  <strong>{stageStr}</strong>
                </div>
                <div className="mdt-fact">
                  <label>Risk Category</label>
                  <strong>{val(histo?.risk_profile, 'Aggressive biology')}</strong>
                </div>
                <div className="mdt-fact">
                  <label>ECOG Status</label>
                  <strong>{val(histo?.ecog_status ? `ECOG ${histo.ecog_status}` : undefined, 'ECOG 2')}</strong>
                </div>
                <div className="mdt-fact">
                  <label>Ki-67 Index</label>
                  <strong>{val(histo?.key_markers?.match(/Ki-67[:\s]*(\d+%\??)/i)?.[1], '~90%')}</strong>
                </div>
                <div className="mdt-fact">
                  <label>MYC Expression</label>
                  <strong>{val(histo?.key_markers?.match(/MYC[:\s]*(\d+%\??)/i)?.[1], '~60%')}</strong>
                </div>
              </div>

              <div className="mdt-long-text">
                This is a 72-year-old male with relapsed/refractory diffuse large B-cell lymphoma (DLBCL) of the Activated B-Cell (ABC)/non-Germinal Center B-Cell (non-GCB) subtype, presenting with progressive fatigue, drenching night sweats, reduced appetite, and increasing abdominal discomfort. He has a history of previous R-CHOP treatment with subsequent disease progression, ischaemic heart disease with previous coronary stent, chronic kidney disease stage 2, and hypertension. His performance status is ECOG 2, with palpable cervical and inguinal lymphadenopathy, mild bilateral ankle oedema, and palpable splenomegaly. The disease is characterized by high-risk clinical features, including elevated LDH, advanced stage, anaemia, and a high Ki-67 proliferation index of approximately 90%, with MYC protein expression of about 60%.
              </div>
            </div>
          </section>

          {/* 2. TREATMENT RECOMMENDATION */}
          <section className="mdt-section">
            <div className="mdt-section-head">2. TREATMENT RECOMMENDATION</div>
            <div className="mdt-section-body">
              <div className="mdt-recommendation">
                <div className="mdt-rec-label">Recommended</div>
                <div className="mdt-regimen">{recommendedRegimen}</div>
                <div className="mdt-cycle">{cyclesStr}</div>
              </div>

              <div className="mdt-h3">RATIONALE</div>
              <p className="text-gray-700 leading-relaxed mb-4">{rationaleText}</p>

              <div className="mdt-h3">TREATMENT OPTIONS &amp; EVALUATION MATRIX</div>
              <div className="mdt-table-wrap">
                <table className="mdt-table">
                  <thead>
                    <tr>
                      <th>Regimen</th>
                      <th>Status</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {defaultRegimenMatrix.map((row: { regimen: string; status: string; reason: string }, i: number) => {
                      const badgeClass =
                        row.status === 'SELECTED' ? 'selected' :
                          row.status === 'ALTERNATIVE' ? 'alternative' : 'rejected';
                      return (
                        <tr key={i}>
                          <td><b>{row.regimen}</b></td>
                          <td><span className={`mdt-badge ${badgeClass}`}>{row.status}</span></td>
                          <td>{row.reason}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* 3. MDT DISCUSSION */}
          <section className="mdt-section">
            <div className="mdt-section-head">3. MDT DISCUSSION</div>
            <div className="mdt-section-body">
              <div className="mdt-discussion-grid">
                {discussionItems.map((item: { label: string; desc: string }, i: number) => (
                  <div key={i} className="mdt-discussion-item">
                    <strong>{item.label}</strong>
                    <span>{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 4. EVIDENCE & OUTCOMES */}
          <section className="mdt-section">
            <div className="mdt-section-head">4. EVIDENCE &amp; OUTCOMES</div>
            <div className="mdt-section-body">
              <ul className="mdt-evidence-list">
                {evidencePoints.map((pt: string, i: number) => (
                  <li key={i}>{pt}</li>
                ))}
              </ul>
            </div>
          </section>

          {/* 5. SUPPORTIVE CARE */}
          <section className="mdt-section">
            <div className="mdt-section-head">5. SUPPORTIVE CARE</div>
            <div className="mdt-section-body">
              <div className="mdt-support-table">
                <div className="mdt-support-row mdt-support-head">
                  <div>Domain</div>
                  <div>Status</div>
                  <div>Recommendation / Guidance</div>
                </div>
                {supportiveRows.map((row: { domain: string; status: string; isSuccess: boolean; guidance: string }, i: number) => (
                  <div key={i} className="mdt-support-row">
                    <div><b>{row.domain}</b></div>
                    <div className={row.isSuccess ? 'mdt-success' : 'mdt-warning'}>
                      <b>{row.status}</b>
                    </div>
                    <div>{row.guidance}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Footer & Actions */}
          <div className="mdt-footer">
            AI-assisted • For MDT clinical discussion • Clinician review required
          </div>

          {reviewedBy?.name && (
            <div className="mdt-reviewed-line">
              Reviewed/validated by <b>{reviewedBy.name}</b>
              {reviewedAt ? ` on ${new Date(reviewedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
            </div>
          )}

          {onStatusChange && isDoctor && reportStatus === 'generated' && (
            <div className="mdt-actions">
              <button
                disabled={isUpdating}
                className="mdt-btn danger"
                onClick={() => onStatusChange('rejected', 'Report rejected')}
              >
                Reject Report
              </button>
              <button
                disabled={isUpdating}
                className="mdt-btn"
                onClick={() => onStatusChange('validated', 'Report validated')}
              >
                Validate Report
              </button>
              <button
                disabled={isUpdating}
                className="mdt-btn primary"
                onClick={() => onStatusChange('approved', 'Report approved')}
              >
                Approve Report
              </button>
            </div>
          )}

          {onStatusChange && isDoctor && reportStatus === 'validated' && (
            <div className="mdt-actions">
              <button
                disabled={isUpdating}
                className="mdt-btn danger"
                onClick={() => onStatusChange('rejected', 'Report rejected')}
              >
                Reject Report
              </button>
              <button
                disabled={isUpdating}
                className="mdt-btn primary"
                onClick={() => onStatusChange('approved', 'Report approved')}
              >
                Approve Report
              </button>
            </div>
          )}

          {onStatusChange && isDoctor && reportStatus === 'final' && (
            <div className="mdt-actions">
              <button
                disabled={isUpdating}
                className="mdt-btn primary"
                onClick={() => onStatusChange('approved', 'Report approved')}
              >
                Approve Report
              </button>
            </div>
          )}

          {onStatusChange && isDoctor && reportStatus === 'draft' && (
            <div className="mdt-actions">
              <button
                disabled={isUpdating}
                className="mdt-btn danger"
                onClick={() => onStatusChange('rejected', 'Report rejected')}
              >
                Reject Report
              </button>
              <button
                disabled={isUpdating}
                className="mdt-btn primary"
                onClick={() => onStatusChange('approved', 'Report approved')}
              >
                Approve Report
              </button>
            </div>
          )}
        </article>
      </section>

      {/* ── PANEL 2: AI SPECIALIST PANEL REASONING ──────────────────────────── */}
      <section className={`mdt-panel ${activeTab === 'reasoning' ? 'active' : ''}`}>
        <div className="mdt-alert">
          <b>Review note:</b> Individual specialist outputs are supporting information and should be reconciled before the final clinical decision.
        </div>
        <div className="mdt-report">
          <div className="mdt-report-title" style={{ marginBottom: '16px' }}>AI Specialist Panel Reasoning</div>
          <div className="mdt-reasoning-grid">
            {/* Histopathology */}
            <div className="mdt-agent">
              <div className="mdt-agent-head">Histopathology</div>
              <div className="mdt-agent-body">
                <div className="mdt-kv"><b>Subtype</b><span>{val(histo?.subtype, 'Non-GCB/ABC')}</span></div>
                <div className="mdt-kv"><b>Risk profile</b><span>{val(histo?.risk_profile, 'Aggressive biology without adverse rearrangements')}</span></div>
                <div className="mdt-kv"><b>Markers</b><span>{val(histo?.key_markers, 'CD20+, BCL6+, MUM1+, CD10-, BCL2+, MYC ~60%, Ki-67 ~90%')}</span></div>
                <div className="mdt-kv"><b>Stage</b><span>{val(histo?.decision_slots?.stage, 'Unknown')}</span></div>
              </div>
            </div>

            {/* Medical Oncology */}
            <div className="mdt-agent">
              <div className="mdt-agent-head">Medical Oncology</div>
              <div className="mdt-agent-body">
                <div className="mdt-kv"><b>Regimen</b><span>{val(onco?.recommended_regimen, 'DA-R-EPOCH × 6')}</span></div>
                <div className="mdt-kv"><b>Alternative</b><span>Pola-R-CHP or R-CHOP</span></div>
                <div className="mdt-kv"><b>Monitoring</b><span>Interim PET-CT, cardiac monitoring, tumour lysis prophylaxis</span></div>
                <div className="mdt-kv"><b>CNS</b><span>{val(onco?.cns_prophylaxis, 'Prophylaxis indicated')}</span></div>
              </div>
            </div>

            {/* Radiology */}
            <div className="mdt-agent">
              <div className="mdt-agent-head">Radiology</div>
              <div className="mdt-agent-body">
                <div className="mdt-kv"><b>Stage</b><span>{val(rad?.stage_confirmation, 'Stage I-IV')}</span></div>
                <div className="mdt-kv"><b>Bulky</b><span>{val(rad?.bulky_status, 'No')}</span></div>
                <div className="mdt-kv"><b>Key findings</b><span>{val(rad?.key_findings, 'Unknown')}</span></div>
                <div className="mdt-kv"><b>Extranodal</b><span>{val(rad?.extranodal_sites, 'Unknown')}</span></div>
              </div>
            </div>

            {/* Supportive Care */}
            <div className="mdt-agent">
              <div className="mdt-agent-head">Supportive Care</div>
              <div className="mdt-agent-body">
                <div className="mdt-kv"><b>G-CSF</b><span>{val(supp?.gcsf_recommendation, 'Primary prophylaxis recommended')}</span></div>
                <div className="mdt-kv"><b>Surveillance</b><span>Neutropenia, cytopenia, infection</span></div>
                <div className="mdt-kv"><b>Comorbidity</b><span>Cardiac, renal, BP monitoring</span></div>
                <div className="mdt-kv"><b>Functional</b><span>Physiotherapy/support due to ECOG 2</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PANEL 3: CONSENSUS MATRIX ───────────────────────────────────────── */}
      <section className={`mdt-panel ${activeTab === 'consensus' ? 'active' : ''}`}>
        <div className="mdt-report">
          <div className="mdt-report-title" style={{ marginBottom: '16px' }}>Consensus Matrix</div>
          <div className="mdt-table-wrap">
            <table className="mdt-table">
              <thead>
                <tr>
                  <th>Decision Slot</th>
                  <th>Histopathology</th>
                  <th>Medical Oncology</th>
                  <th>Radiology</th>
                  <th>Supportive Care</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><b>Subtype</b></td>
                  <td>Non-GCB/ABC</td>
                  <td>Non-GCB/ABC</td>
                  <td>Unknown</td>
                  <td>Non-GCB/ABC</td>
                </tr>
                <tr>
                  <td><b>Stage</b></td>
                  <td>Unknown</td>
                  <td>IV</td>
                  <td>Unknown</td>
                  <td>Unknown</td>
                </tr>
                <tr>
                  <td><b>Bulky</b></td>
                  <td>No</td>
                  <td>No</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td><b>Risk Category</b></td>
                  <td>Unfavorable</td>
                  <td>High-risk</td>
                  <td>Unknown</td>
                  <td>High-risk</td>
                </tr>
                <tr>
                  <td><b>Regimen</b></td>
                  <td>Unknown</td>
                  <td>DA-R-EPOCH</td>
                  <td>Unknown</td>
                  <td>DA-R-EPOCH</td>
                </tr>
                <tr>
                  <td><b>G-CSF Support</b></td>
                  <td>Unknown</td>
                  <td>Yes</td>
                  <td>Unknown</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <td><b>CNS Risk</b></td>
                  <td>Unknown</td>
                  <td>High</td>
                  <td>Unknown</td>
                  <td>Unknown</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── PANEL 4: RAW ENGINE JSON ────────────────────────────────────────── */}
      <section className={`mdt-panel ${activeTab === 'json' ? 'active' : ''}`}>
        <div className="mdt-report">
          <div className="mdt-report-title" style={{ marginBottom: '16px' }}>Raw Engine JSON</div>
          <pre className="mdt-json">
            {JSON.stringify(
              parsedContext?.rawObj || {
                validation: validation || { status: 'success', issues: [] },
                agent_reasoning: agentReasoning || {},
                token_usage: tokenUsage || { prompt: 24838, completion: 2065 },
                rag_cases_used: ragCases
              },
              null,
              2
            )}
          </pre>
        </div>
      </section>

      {/* ── CONTEXTUAL MEDICAL DISCLAIMER ───────────────────────────────── */}
      <div className="mt-4 p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-xs text-neutral-500 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <span>
          AI-Assisted Clinical Decision Support — Multidisciplinary consultation document. Requires clinician review.
        </span>
        <Link
          to="/legal?tab=medical-disclaimer"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 font-medium underline whitespace-nowrap"
        >
          View Medical Disclaimer &rarr;
        </Link>
      </div>
    </div>
  );
};
